import * as React from 'react'

import { type VirtualItem } from '@tanstack/react-virtual'
import { SlidersVerticalIcon, SplitIcon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import VirtualizedList from '~/client/src/shared/components/VirtualizedList/VirtualizedList'
import WorkflowStepNameLabel from '~/client/src/shared/components/WorkflowStepNameLabel'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IFieldArgumentOption } from '../WorkflowActionConfigurator.store'

interface IProps {
  stepActionFieldIds: string[]
  actionArgumentOptions: IFieldArgumentOption[]
  onChange(fieldIds: string[]): void
  renderTrailingContent?: (option: IFieldArgumentOption) => JSX.Element
  onCheckboxClick?: (option: IFieldArgumentOption) => void
  isCheckboxChecked?: (option: IFieldArgumentOption) => boolean
}

const MODAL_HEIGHT_RATIO = 0.4
const ROW_HEIGHT = 36

@observer
export default class ActionFieldSelector extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    return (
      <Popover>
        <PopoverTrigger
          nativeButton={false}
          render={
            <div className="tw:flex tw:items-center full-height ba-light-grey brada4 pa10 pointer" />
          }
        >
          <span className="text large tw:flex-1">{this.title}</span>
          <SlidersVerticalIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
        </PopoverTrigger>
        <PopoverContent
          backdrop
          side="bottom"
          align="start"
          className="tw:w-[500px] tw:overflow-hidden tw:p-0"
        >
          {this.fieldsList}
        </PopoverContent>
      </Popover>
    )
  }

  private get fieldsList(): JSX.Element {
    const { actionArgumentOptions } = this.props

    return (
      <div className="col no-outline-container">
        <span className="text large line-extra-large pt20 px24">
          {Localization.translator.selectFields}
        </span>
        <div className="pt10 pb20 virtualized-list-smart-wrapper px24">
          <VirtualizedList
            items={actionArgumentOptions}
            itemHeight={ROW_HEIGHT}
            containerHeight={Math.min(
              window.innerHeight * MODAL_HEIGHT_RATIO,
              ROW_HEIGHT * actionArgumentOptions.length,
            )}
            renderItem={this.renderOption}
            noItemsRenderer={() => (
              <div className="text center large bold py20">
                {Localization.translator.none}
              </div>
            )}
          />
        </div>
      </div>
    )
  }

  private renderOption = (
    option: IFieldArgumentOption,
    virtualItem: VirtualItem,
  ): JSX.Element => {
    if (!option) return null

    const {
      stepName,
      stepPosition,
      field,
      fieldPosition,
      subFieldPosition,
      shouldIndent,
      isConditional,
      isDisabled,
    } = option

    return (
      <div
        key={String(virtualItem.key)}
        className="row y-center tw:gap-2.5 bb-light-grey px24 h36"
      >
        {stepName ? (
          <WorkflowStepNameLabel
            stepPosition={stepPosition}
            stepName={stepName}
          />
        ) : (
          <>
            <span
              className={classList({
                'text large ellipsis no-flex': true,
                ml10: shouldIndent,
              })}
            >
              {isConditional ? (
                <SplitIcon className="tw:size-5 tw:text-muted-foreground" />
              ) : (
                [stepPosition, fieldPosition, subFieldPosition]
                  .filter(p => p)
                  .join('.')
              )}
            </span>
            <PermitFieldIcon fieldName={field.type} />
            <span className="text large ellipsis" title={field.caption}>
              {field.caption}
            </span>
            {this.localProps.renderTrailingContent?.(option)}
            {!isDisabled && (
              <Checkbox
                checked={this.isCheckboxChecked(option)}
                onCheckedChange={() => this.handleClick(option)}
              />
            )}
          </>
        )}
      </div>
    )
  }

  private get title(): string {
    const { stepActionFieldIds } = this.props
    if (!stepActionFieldIds?.length) {
      return Localization.translator.none
    }
    return `${
      stepActionFieldIds.length
    } ${Localization.translator.selected.toLowerCase()}`
  }

  private isCheckboxChecked = (option: IFieldArgumentOption): boolean => {
    const { isCheckboxChecked, stepActionFieldIds } = this.localProps
    return isCheckboxChecked
      ? isCheckboxChecked(option)
      : stepActionFieldIds.includes(option.field?.id)
  }

  private handleClick(option: IFieldArgumentOption) {
    if (option.isDisabled || !option.field?.id) return

    if (this.props.onCheckboxClick) {
      return this.props.onCheckboxClick(option)
    }

    const fieldId = option.field.id

    if (this.props.stepActionFieldIds.includes(fieldId))
      this.props.onChange(
        this.props.stepActionFieldIds.filter(id => id !== fieldId),
      )
    else this.props.onChange([...this.props.stepActionFieldIds, fieldId])
  }
}
